/**
 * ExamHistoryImportPage — vendor-aware exam-history importer.
 * CLARID-SPEC-MIG-004.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { examImportApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

const VENDORS = [
  { value: 'NCONTRACTS', label: 'Ncontracts' },
  { value: 'WOLTERS', label: 'Wolters Kluwer' },
  { value: 'CROWE', label: 'Crowe' },
  { value: 'GENERIC', label: 'Generic (auto-map)' },
] as const;

interface MappedExam { rowIndex: number; fields: Record<string, unknown>; warnings: string[]; errors: string[]; }
interface MappedFinding { rowIndex: number; fields: Record<string, unknown>; warnings: string[]; errors: string[]; }

interface Preview {
  detectedVendor: string;
  detectedConfidence: number;
  selectedVendor: string;
  fileShape: 'single-table' | 'two-sheet';
  sheetNames: string[];
  exams: MappedExam[];
  findings: MappedFinding[];
  skippedExamRows: number[];
  skippedFindingRows: number[];
  totalExams: number;
  totalFindings: number;
  unresolvedPriorLinks: number;
  errorExamCount: number;
  errorFindingCount: number;
  warningCount: number;
}

export default function ExamHistoryImportPage() {
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mergeWithExisting, setMergeWithExisting] = useState(false);

  const previewMutation = useMutation({
    mutationFn: async () => examImportApi.preview(teamId!, file!),
    onSuccess: (res) => {
      setPreviewId(res.data.previewId);
      setPreview(res.data.preview);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Preview failed'),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => examImportApi.updatePreview(teamId!, previewId!, data),
    onSuccess: (res) => setPreview(res.data.preview),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Update failed'),
  });

  const commitMutation = useMutation({
    mutationFn: async () => examImportApi.commit(teamId!, previewId!, {
      mergeWithExistingExams: mergeWithExisting,
    }),
    onSuccess: (res) => {
      const { examsCreated, examsMerged, findingsCreated, priorLinksResolved } = res.data;
      toast.success(`${examsCreated} exams + ${findingsCreated} findings imported (${priorLinksResolved} prior links resolved${examsMerged ? `, ${examsMerged} merged` : ''})`);
      navigate('/exams');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Import failed'),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate('/exams')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Exams
      </button>

      <h1 className="text-2xl font-bold text-navy-900">Bulk import exam history</h1>
      <p className="mt-1 text-sm text-gray-500">
        Import historical exams + findings + remediation tracking from a competitor compliance vendor.
        Supports two-sheet XLSX (Exams + Findings) and single-table CSV/XLSX. Prior-finding recurrence
        links are preserved when present in the source.
      </p>

      {!preview && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV or XLSX</label>
          <input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100"
          />
          <button
            onClick={() => previewMutation.mutate()}
            disabled={!file || previewMutation.isPending}
            className="mt-4 inline-flex items-center px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded-md hover:bg-navy-800 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            {previewMutation.isPending ? 'Detecting…' : 'Detect & preview'}
          </button>
        </div>
      )}

      {preview && (
        <div className="mt-6 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Detected vendor · {preview.fileShape}</div>
                <div className="mt-1 text-xl font-bold text-navy-900">
                  {VENDORS.find(v => v.value === preview.selectedVendor)?.label}
                  <span className="ml-2 text-sm font-normal text-gray-500">{(preview.detectedConfidence * 100).toFixed(0)}% confidence</span>
                </div>
                {preview.sheetNames.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">Sheets: {preview.sheetNames.join(', ')}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Override vendor</label>
                <select
                  value={preview.selectedVendor}
                  onChange={(e) => updateMutation.mutate({ vendorOverride: e.target.value })}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
                >
                  {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat label="Exams" value={preview.totalExams} />
              <Stat label="Findings" value={preview.totalFindings} />
              <Stat label="Exam errors" value={preview.errorExamCount} tone={preview.errorExamCount > 0 ? 'bad' : 'neutral'} />
              <Stat label="Finding errors" value={preview.errorFindingCount} tone={preview.errorFindingCount > 0 ? 'bad' : 'neutral'} />
              <Stat label="Unresolved links" value={preview.unresolvedPriorLinks} tone={preview.unresolvedPriorLinks > 0 ? 'warn' : 'neutral'} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-navy-900">Exams ({preview.exams.length})</h3>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Agency</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Start Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.exams.slice(0, 30).map(e => (
                    <tr key={e.rowIndex} className={e.errors.length > 0 ? 'bg-red-50' : ''}>
                      <td className="px-4 py-2">{e.rowIndex + 1}</td>
                      <td className="px-4 py-2">{String(e.fields.examType ?? '—')}</td>
                      <td className="px-4 py-2 font-medium">{String(e.fields.examinerAgency ?? '—')}</td>
                      <td className="px-4 py-2">{e.fields.scheduledStartDate ? new Date(String(e.fields.scheduledStartDate)).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-2">{String(e.fields.status ?? '—')}</td>
                      <td className="px-4 py-2">
                        {e.errors.length > 0 ? <span className="text-red-600">{e.errors[0]}</span> :
                         e.warnings.length > 0 ? <span className="text-amber-600 text-[10px]">{e.warnings[0]}</span> :
                         <CheckCircleIcon className="h-4 w-4 text-green-600" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-navy-900">Findings ({preview.findings.length})</h3>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.findings.slice(0, 50).map(f => (
                    <tr key={f.rowIndex} className={f.errors.length > 0 ? 'bg-red-50' : ''}>
                      <td className="px-4 py-2">{f.rowIndex + 1}</td>
                      <td className="px-4 py-2">{String(f.fields.findingType ?? '—')}</td>
                      <td className="px-4 py-2">{String(f.fields.severity ?? '—')}</td>
                      <td className="px-4 py-2">{String(f.fields.category ?? '—')}</td>
                      <td className="px-4 py-2 max-w-md truncate text-gray-600">{String(f.fields.description ?? '').slice(0, 100)}</td>
                      <td className="px-4 py-2">{String(f.fields.remediationStatus ?? '—')}</td>
                      <td className="px-4 py-2">
                        {f.errors.length > 0 ? <XCircleIcon className="h-4 w-4 text-red-600" /> :
                         f.warnings.length > 0 ? <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" /> :
                         <CheckCircleIcon className="h-4 w-4 text-green-600" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={mergeWithExisting}
                onChange={(e) => setMergeWithExisting(e.target.checked)}
                className="rounded border-gray-300"
              />
              Merge findings into existing exams when agency + start date match (±7 days)
            </label>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-gray-500">
                Importing {preview.totalExams - preview.errorExamCount} exams and {preview.totalFindings - preview.errorFindingCount} findings.
              </p>
              <button
                onClick={() => commitMutation.mutate()}
                disabled={commitMutation.isPending || (preview.errorExamCount === preview.totalExams)}
                className="inline-flex items-center px-6 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-md hover:bg-navy-800 disabled:opacity-50"
              >
                {commitMutation.isPending ? 'Importing…' : 'Import exams and findings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const color = tone === 'good' ? 'text-green-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-red-600' : 'text-navy-900';
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
